// deno-lint-ignore-file no-explicit-any
// TODO(PWA migration): remove when the reused report-policy/view JS helpers expose TypeScript declarations.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  dayStartProposalSourceLabel,
  formatReportClock,
  staffingDisplayState,
} from "../../report-day-start-view.js";
import { buildPdfFilename, projectCodeFromContext } from "../../pdf-filename.js";
import { createAdaptiveReportPoller } from "../../report-poller.js";
import {
  getReportDeleteAction,
  getReportPurgeAction,
  getReportReopenAction,
} from "../../report-reopen-policy.js";
import { buildWorkPackageView, formatReportHours } from "../../report-work-package-view.js";
import { formatTransportWeight, hasAnyTransportWeight } from "../../transport-weight.js";
import { AppShell } from "../components/AppShell";
import { ReportNotesSection } from "../components/ReportNotesSection";
import { WorkPackagePhotoGallery } from "../components/WorkPackagePhotoGallery";
import { getFunctionUrl } from "../config";
import { api } from "../lib/api";
import { signedAssetPreviewUrls } from "../lib/assetPreviews";
import { signedAssetUrlState } from "../lib/assets";
import { getAuthToken } from "../lib/auth";
import type { AccessContext, DayReport, ReportAsset } from "../types/api";

const STATUS_LABEL: Record<string, string> = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
};

const PRESENCE_LABEL: Record<string, string> = {
  present: "Aanwezig",
  absent: "Afwezig",
  partial: "Gedeeltelijk",
  unknown: "Onbekend",
};

const DAY_START_SECTIONS = [
  { key: "staffing", label: "Personeel", field: "staffing_confirmed_at" },
  { key: "equipment", label: "Materieel", field: "equipment_confirmed_at" },
  {
    key: "work_assignments",
    label: "Werkzaamheden",
    field: "work_assignments_confirmed_at",
  },
] as const;

function dayStartNextStep(report: DayReport): string {
  if (report.status !== "draft") {
    return "Dit rapport is alleen-lezen; de dagstart kan niet worden gewijzigd.";
  }
  if (!report.staffing_confirmed_at) {
    return "Volgende stap: personeelsbezetting bevestigen.";
  }
  if (!report.equipment_confirmed_at) {
    return "Volgende stap: materieelbezetting bevestigen.";
  }
  if (!report.work_assignments_confirmed_at) {
    return "Volgende stap: werkzaamheden bevestigen.";
  }
  if (report.day_started_at) {
    return "Dagstart gereed. Live aanvullingen kunnen worden geregistreerd.";
  }
  return "De drie onderdelen zijn bevestigd; de afrondingsstatus wordt bijgewerkt.";
}

function assetKey(asset: any): string {
  return asset.report_asset_id || asset.storage_path || asset.filename;
}

function assetWorkOwners(report: DayReport): Map<string, string> {
  const owners = new Map<string, string>();
  const work = report.work_assignments ?? [];
  for (const asset of report.assets ?? []) {
    const key = assetKey(asset);
    if (!key) continue;
    if (asset.work_assignment_id) {
      owners.set(key, asset.work_assignment_id);
      continue;
    }
    if (!asset.caption) continue;
    const caption = asset.caption.toLowerCase();
    const owner = work.find((item: any) =>
      item.work_assignment_id && item.description &&
      caption.includes(item.description.toLowerCase())
    );
    if (owner?.work_assignment_id) owners.set(key, owner.work_assignment_id);
  }
  return owners;
}

function linkedAssets(
  report: DayReport,
  work: any,
  owners: Map<string, string>,
): ReportAsset[] {
  return (report.assets ?? []).filter((asset: any) =>
    owners.get(assetKey(asset)) === work.work_assignment_id
  );
}

function PhotoPreview({
  asset,
  originalUrl,
  previewUrl,
  archiveUnavailable,
  className,
}: {
  asset: any;
  originalUrl: string | null;
  previewUrl: string | null;
  archiveUnavailable: boolean;
  className: string;
}) {
  const previewImage = previewUrl
    ? (
      <img
        src={previewUrl}
        alt={asset.filename}
        className={className}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        width={160}
        height={120}
      />
    )
    : null;

  if (originalUrl) {
    return (
      <a
        href={originalUrl}
        target="_blank"
        rel="noreferrer"
        title={asset.caption || asset.filename}
      >
        {previewImage || <span className="badge muted">Foto openen</span>}
      </a>
    );
  }
  if (previewImage) return previewImage;
  if (archiveUnavailable) {
    return <span className="badge muted">Dropbox-archief tijdelijk niet bereikbaar</span>;
  }
  return <span className="badge muted">Geen preview</span>;
}

export function ReportPage() {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("id");
  const [report, setReport] = useState<DayReport | null>(null);
  const [accessContext, setAccessContext] = useState<AccessContext | null>(null);
  const [assetUrls, setAssetUrls] = useState<Map<string, string>>(new Map());
  const [assetPreviewUrls, setAssetPreviewUrls] = useState<Map<string, string>>(new Map());
  const [assetArchiveUnavailablePaths, setAssetArchiveUnavailablePaths] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const pollerRef = useRef<any>(null);

  const applyPolledReport = useCallback(async (nextReport: DayReport) => {
    const [urlState, previewUrls] = await Promise.all([
      signedAssetUrlState(nextReport),
      signedAssetPreviewUrls(nextReport),
    ]);
    setReport(nextReport);
    setAssetUrls(urlState.urls);
    setAssetPreviewUrls(previewUrls);
    setAssetArchiveUnavailablePaths(urlState.archiveUnavailablePaths);
    setLastUpdated(new Date());
  }, []);

  const refresh = useCallback(async (silent = false) => {
    if (!reportId) {
      setError("Rapport-id ontbreekt");
      setLoading(false);
      return;
    }
    if (silent && pollerRef.current) {
      pollerRef.current.refresh();
      return;
    }
    try {
      if (!silent) setLoading(true);
      const nextReport = await api.getReport(reportId);
      const [urlState, previewUrls, context] = await Promise.all([
        signedAssetUrlState(nextReport),
        signedAssetPreviewUrls(nextReport),
        accessContext ? Promise.resolve(accessContext) : api.getAccessContext(),
      ]);
      setReport(nextReport);
      setAssetUrls(urlState.urls);
      setAssetPreviewUrls(previewUrls);
      setAssetArchiveUnavailablePaths(urlState.archiveUnavailablePaths);
      setAccessContext(context);
      setLastUpdated(new Date());
      setError(null);
    } catch (cause) {
      if (!silent) {
        setError(cause instanceof Error ? cause.message : "Onbekende fout");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [reportId, accessContext]);

  useEffect(() => {
    void refresh();
  }, [reportId]);

  useEffect(() => {
    if (!reportId || !report || report.status === "approved") return;
    const poller = createAdaptiveReportPoller({
      fetchReport: (activeReportId: string, signal: AbortSignal) =>
        api.getReport(activeReportId, signal),
      onReport: (nextReport) => {
        void applyPolledReport(nextReport as DayReport);
      },
      onError: (cause: unknown) => {
        console.warn("Rapport kon niet automatisch worden ververst", cause);
      },
    });
    pollerRef.current = poller;
    poller.start(reportId);
    return () => {
      if (pollerRef.current === poller) pollerRef.current = null;
      poller.destroy();
    };
  }, [reportId, report?.status, applyPolledReport]);

  const workPackages = useMemo(
    () => report ? buildWorkPackageView(report) : [],
    [report],
  );
  const showTransportWeight = useMemo(
    () => report ? hasAnyTransportWeight(report.transport_entries ?? []) : false,
    [report],
  );
  const proposals = useMemo(
    () =>
      new Map(
        (report?.day_start_proposals ?? []).map((
          proposal: any,
        ) => [proposal.section_type, proposal]),
      ),
    [report],
  );
  const staffingDisplay = useMemo(
    () => report ? staffingDisplayState(report) : { rows: [], note: "", isProposal: false },
    [report],
  );
  const photoOwners = useMemo(
    () => report ? assetWorkOwners(report) : new Map<string, string>(),
    [report],
  );
  const unlinkedAssets = useMemo(
    () => (report?.assets ?? []).filter((asset: any) => !photoOwners.has(assetKey(asset))),
    [report, photoOwners],
  );
  const reportActions = useMemo(() => {
    if (!report || !accessContext) return [] as any[];
    return [
      getReportReopenAction(report, accessContext),
      getReportDeleteAction(report, accessContext),
      getReportPurgeAction(report, accessContext),
    ].filter(Boolean) as any[];
  }, [report, accessContext]);

  async function mutate(label: string, operation: () => Promise<DayReport>) {
    setBusy(label);
    try {
      const updated = await operation();
      await applyPolledReport(updated);
      pollerRef.current?.markMutation({ refresh: false });
    } catch (cause) {
      globalThis.alert(
        `Actie mislukt: ${cause instanceof Error ? cause.message : "Onbekende fout"}`,
      );
    } finally {
      setBusy(null);
    }
  }

  async function movePhoto(asset: ReportAsset, targetWorkAssignmentId: string) {
    if (!report) throw new Error("Rapport is niet geladen.");
    setBusy("move-photo");
    try {
      const updated = await api.moveReportAsset(
        report.day_report_id,
        report.revision,
        asset,
        targetWorkAssignmentId,
      );
      await applyPolledReport(updated);
      pollerRef.current?.markMutation({ refresh: false });
    } catch (cause) {
      globalThis.alert(
        `Foto kon niet worden verplaatst: ${
          cause instanceof Error ? cause.message : "Onbekende fout"
        }`,
      );
      throw cause;
    } finally {
      setBusy(null);
    }
  }

  async function executePolicyAction(action: any) {
    if (!report) return;
    const rawReason = globalThis.prompt(action.prompt);
    if (rawReason === null) return;
    const reason = rawReason.trim();
    if (!reason) {
      return globalThis.alert(
        "Vul een reden in. De reden is verplicht voor de audittrail.",
      );
    }
    if (action.confirmation && !globalThis.confirm(action.confirmation)) return;
    if (action.requiresReportDateConfirmation) {
      const date = globalThis.prompt(
        `Typ de rapportdatum ${report.report_date} om definitief verwijderen te bevestigen.`,
      );
      if (date === null) return;
      if (date.trim() !== report.report_date) {
        return globalThis.alert(
          "De ingevoerde rapportdatum komt niet overeen. Er is niets verwijderd.",
        );
      }
    }

    setBusy(action.kind);
    try {
      if (action.kind === "withdraw") {
        const updated = await api.withdrawReport(
          report.day_report_id,
          report.revision,
          reason,
        );
        await applyPolledReport(updated);
        pollerRef.current?.markMutation({ refresh: false });
      } else if (action.kind === "reopen-approved") {
        const updated = await api.reopenApprovedReport(
          report.day_report_id,
          report.revision,
          reason,
        );
        await applyPolledReport(updated);
        pollerRef.current?.markMutation({ refresh: false });
      } else if (action.kind === "delete") {
        await api.deleteReport(report.day_report_id, report.revision, reason);
        globalThis.location.assign("/home/");
      } else {
        await api.purgeReport(
          report.day_report_id,
          report.revision,
          report.report_date,
          reason,
        );
        globalThis.location.assign("/home/");
      }
    } catch (cause) {
      globalThis.alert(
        `Rapport kon niet worden verwerkt: ${
          cause instanceof Error ? cause.message : "Onbekende fout"
        }`,
      );
    } finally {
      setBusy(null);
    }
  }

  async function downloadPdf() {
    if (!report) return;
    setBusy("pdf");
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Niet ingelogd");
      const response = await fetch(
        getFunctionUrl("dbvdw-pdf", `?day_report_id=${report.day_report_id}`),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        throw new Error(`PDF kon niet worden gegenereerd (HTTP ${response.status}).`);
      }
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildPdfFilename({
        projectCode: accessContext
          ? projectCodeFromContext(accessContext, report.project_id)
          : null,
        reportDate: report.report_date,
        revision: report.revision,
      });
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (cause) {
      globalThis.alert(
        `Fout bij downloaden PDF: ${cause instanceof Error ? cause.message : "Onbekende fout"}`,
      );
    } finally {
      setBusy(null);
    }
  }

  const actionBar = report
    ? (
      <div className="actions">
        {report.status === "draft" && (
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() =>
              void mutate("submit", () => api.submitReport(report.day_report_id, report.revision))}
          >
            Indienen
          </button>
        )}
        {report.status === "submitted" && (
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() =>
              void mutate(
                "approve",
                () => api.approveReport(report.day_report_id, report.revision),
              )}
          >
            Goedkeuren
          </button>
        )}
        <button
          type="button"
          className="secondary"
          disabled={Boolean(busy)}
          onClick={() => void downloadPdf()}
        >
          PDF downloaden
        </button>
        {reportActions.map((action) => (
          <button
            key={action.kind}
            type="button"
            className="secondary report-reopen-action"
            disabled={Boolean(busy)}
            onClick={() => void executePolicyAction(action)}
          >
            {action.label}
          </button>
        ))}
      </div>
    )
    : null;

  return (
    <AppShell>
      <main className="panel">
        <Link to="/home/" className="back-btn">← Terug naar verslagen</Link>
        {loading && (
          <section>
            <h1>Verslag laden...</h1>
          </section>
        )}
        {error && (
          <section>
            <p className="error">Fout: {error}</p>
          </section>
        )}
        {!loading && report && (
          <section>
            <div className="report-header">
              <div>
                <h1>Verslag {report.report_date}</h1>
                <span className={`report-status status-${report.status}`}>
                  {STATUS_LABEL[report.status] ?? report.status}
                </span>
              </div>
            </div>
            <div className="actions top-actions">{actionBar?.props.children}</div>

            <div className="section">
              <h2>Dagstart</h2>
              <p className="muted">{dayStartNextStep(report)}</p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Onderdeel</th>
                    <th>Status</th>
                    <th>Bron voorstel</th>
                  </tr>
                </thead>
                <tbody>
                  {DAY_START_SECTIONS.map((section) => {
                    const proposal = proposals.get(section.key) as any;
                    const confirmed = Boolean(report[section.field]);
                    const hasProposal = Boolean(proposal?.snapshot?.length);
                    return (
                      <tr key={section.key}>
                        <td>{section.label}</td>
                        <td>
                          {confirmed
                            ? <span className="badge badge-completed">Bevestigd</span>
                            : hasProposal
                            ? <span className="badge badge-in_progress">Voorstel klaar</span>
                            : <span className="badge muted">Nog nodig</span>}
                        </td>
                        <td>{dayStartProposalSourceLabel(proposal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="section">
              <h2>Teamleden</h2>
              <p className="muted">{staffingDisplay.note}</p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Naam</th>
                    <th>Status</th>
                    <th>Afwezigheid</th>
                    <th>Start</th>
                    <th>Einde</th>
                  </tr>
                </thead>
                <tbody>
                  {staffingDisplay.rows.length
                    ? staffingDisplay.rows.map((member: any) => (
                      <tr key={member.project_employee_id ?? member.employee_name}>
                        <td>
                          {member.employee_name || member.project_employee_id}
                          {member.role && (
                            <span
                              className="badge muted"
                              style={{ marginLeft: ".35rem", fontSize: ".75rem" }}
                            >
                              {member.role}
                            </span>
                          )}
                        </td>
                        <td>{PRESENCE_LABEL[member.presence] ?? member.presence}</td>
                        <td>{member.absence_category || "-"}</td>
                        <td>{formatReportClock(member.actual_start_time)}</td>
                        <td>{formatReportClock(member.actual_end_time)}</td>
                      </tr>
                    ))
                    : (
                      <tr>
                        <td colSpan={5} className="empty-state">Geen teamleden</td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            <div className="section" id="work-packages">
              <h2>Werkpakketten</h2>
              {workPackages.length
                ? (
                  <div className="work-package-stack">
                    {workPackages.map((workPackage: any) => {
                      const workItems = workPackage.assignments.map((work: any) => ({
                        work,
                        photos: linkedAssets(report, work, photoOwners),
                      }));
                      const photoCount = workItems.reduce(
                        (total: number, item: any) => total + item.photos.length,
                        0,
                      );
                      return (
                        <article className="work-package-card" key={workPackage.workPackage}>
                          <div className="work-package-card__header">
                            <h3>{workPackage.workPackage}</h3>
                            <span className="badge badge-wp">
                              {workPackage.assignments.length}{" "}
                              werkzaamhe{workPackage.assignments.length === 1 ? "id" : "den"}
                            </span>
                          </div>

                          <div className="work-package-block">
                            <h4>Urenverdeling</h4>
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Medewerker</th>
                                  <th>Uren</th>
                                </tr>
                              </thead>
                              <tbody>
                                {workPackage.hours.length
                                  ? workPackage.hours.map((row: any, index: number) => (
                                    <tr key={`${row.employeeName}-${index}`}>
                                      <td>{row.employeeName}</td>
                                      <td>{formatReportHours(row.minutes)} uur</td>
                                    </tr>
                                  ))
                                  : (
                                    <tr>
                                      <td colSpan={2} className="empty-state">
                                        Geen uren geregistreerd
                                      </td>
                                    </tr>
                                  )}
                              </tbody>
                            </table>
                          </div>

                          <div className="work-package-block">
                            <h4>Werkzaamheden en medewerkers</h4>
                            <div className="work-assignment-overview">
                              {workPackage.assignments.length
                                ? workPackage.assignments.map((work: any) => (
                                  <div
                                    className="work-assignment-overview__item"
                                    key={work.work_assignment_id || work.description}
                                  >
                                    <strong>{work.description || "Werkzaamheid"}</strong>
                                    <div className="work-assignment-overview__members">
                                      {work.employeeNames.length
                                        ? work.employeeNames.map((name: string) => (
                                          <span className="badge muted" key={name}>{name}</span>
                                        ))
                                        : <span className="muted">Geen medewerkers gekoppeld</span>}
                                      {work.classification && (
                                        <span className="badge badge-wp">
                                          {work.classification}
                                        </span>
                                      )}
                                      <span
                                        className={`badge badge-${work.status || "in_progress"}`}
                                      >
                                        {work.status === "completed" ? "Afgerond" : "Lopend"}
                                      </span>
                                    </div>
                                  </div>
                                ))
                                : <p className="muted">Geen werkzaamheden geregistreerd.</p>}
                            </div>
                          </div>

                          <div className="work-package-block">
                            <h4>Werkbeschrijvingen</h4>
                            {workPackage.assignments.length
                              ? (
                                <ul>
                                  {workPackage.assignments.map((work: any) => (
                                    <li key={work.work_assignment_id || work.description}>
                                      {work.description || "Werkzaamheid zonder omschrijving"}
                                    </li>
                                  ))}
                                </ul>
                              )
                              : <p className="muted">Geen werkbeschrijvingen geregistreerd.</p>}
                          </div>

                          <div className="work-package-block">
                            <h4>Foto's bij de werkzaamheden</h4>
                            {photoCount
                              ? (
                                <WorkPackagePhotoGallery
                                  workItems={workItems}
                                  allWorkAssignments={report.work_assignments ?? []}
                                  assetUrls={assetUrls}
                                  assetPreviewUrls={assetPreviewUrls}
                                  archiveUnavailablePaths={assetArchiveUnavailablePaths}
                                  editable={report.status === "draft"}
                                  busy={Boolean(busy)}
                                  onMove={movePhoto}
                                />
                              )
                              : <p className="muted">Geen foto's aan dit werkpakket gekoppeld.</p>}
                          </div>

                          <div className="work-package-block">
                            <h4>Materieel</h4>
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Materieel</th>
                                  <th>Machinist</th>
                                  <th>Draaiuren</th>
                                </tr>
                              </thead>
                              <tbody>
                                {workPackage.equipment.length
                                  ? workPackage.equipment.map((row: any, index: number) => (
                                    <tr key={`${row.equipmentName}-${row.machinistName}-${index}`}>
                                      <td>{row.equipmentName}</td>
                                      <td>{row.machinistName}</td>
                                      <td>
                                        {formatReportHours(row.minutes)}
                                        {row.minutes === null ? "" : " uur"}
                                      </td>
                                    </tr>
                                  ))
                                  : (
                                    <tr>
                                      <td colSpan={3} className="empty-state">
                                        Geen materieel geregistreerd
                                      </td>
                                    </tr>
                                  )}
                              </tbody>
                            </table>
                          </div>

                          <div className="work-package-block">
                            <h4>Samenvatting voor ERP</h4>
                            <p className="erp-summary">{workPackage.summary}</p>
                          </div>

                          <div className="work-package-block">
                            <h4>Detailreview invoer</h4>
                            {workPackage.reviewLines.length
                              ? (
                                <ul className="detail-review-list">
                                  {workPackage.reviewLines.map((line: string, index: number) => (
                                    <li key={`${line}-${index}`}>{line}</li>
                                  ))}
                                </ul>
                              )
                              : <p className="muted">Geen medewerkergebonden detailregels.</p>}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )
                : <p className="empty-state">Geen werkpakketten geregistreerd.</p>}
            </div>

            <div className="section">
              <h2>Transportregistratie</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Richting</th>
                    <th>Materiaal</th>
                    <th>Vrachten</th>
                    {showTransportWeight && <th>Gewicht</th>}
                    <th>Persoon/bedrijf</th>
                    <th>Chauffeur</th>
                    <th>Kenteken</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.transport_entries ?? []).length
                    ? (report.transport_entries ?? []).map((entry: any, index: number) => (
                      <tr key={entry.day_report_transport_id ?? index}>
                        <td>{entry.direction === "outbound" ? "Uitgaand" : "Inkomend"}</td>
                        <td>{entry.material_name || "-"}</td>
                        <td>{entry.load_count ?? "-"}</td>
                        {showTransportWeight && <td>{formatTransportWeight(entry)}</td>}
                        <td>{entry.carrier_name || "-"}</td>
                        <td>{entry.driver_name || "-"}</td>
                        <td>{entry.license_plate || "-"}</td>
                      </tr>
                    ))
                    : (
                      <tr>
                        <td colSpan={showTransportWeight ? 7 : 6} className="empty-state">
                          Geen transport
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            <ReportNotesSection report={report} />

            <div className="section">
              <h2>Niet gekoppelde foto's &amp; bijlagen</h2>
              <p className="muted">
                Foto's die aan een werkzaamheid gekoppeld zijn staan hierboven bij het juiste
                werkpakket.
              </p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Preview</th>
                    <th>Bestandsnaam</th>
                    <th>Omschrijving</th>
                    <th>Volgorde</th>
                  </tr>
                </thead>
                <tbody>
                  {unlinkedAssets.length
                    ? unlinkedAssets.map((asset: any) => {
                      const original = asset.storage_path
                        ? assetUrls.get(asset.storage_path) ?? null
                        : null;
                      const preview = asset.storage_path
                        ? assetPreviewUrls.get(asset.storage_path) ?? null
                        : null;
                      return (
                        <tr key={assetKey(asset)}>
                          <td>
                            <PhotoPreview
                              asset={asset}
                              originalUrl={original}
                              previewUrl={preview}
                              archiveUnavailable={Boolean(
                                asset.storage_path &&
                                  assetArchiveUnavailablePaths.has(asset.storage_path),
                              )}
                              className="asset-preview-img"
                            />
                          </td>
                          <td>{asset.filename}</td>
                          <td>{asset.caption || "-"}</td>
                          <td>{asset.display_order ?? 0}</td>
                        </tr>
                      );
                    })
                    : (
                      <tr>
                        <td colSpan={4} className="empty-state">
                          Geen losse foto's of bijlagen
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            {actionBar}
            <div className="refresh-indicator">
              <button
                type="button"
                className="secondary"
                disabled={Boolean(busy)}
                onClick={() => void refresh(true)}
              >
                Verversen
              </button>
              <span>
                {lastUpdated ? `Bijgewerkt om ${lastUpdated.toLocaleTimeString("nl-NL")}` : ""}
              </span>
            </div>
          </section>
        )}
      </main>
    </AppShell>
  );
}
